/**
 * Кнопки действий для админа смены.
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export function ActionButtons({ order, onConfirm, onCancel, isLoading }) {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const canConfirm = order.status === 'pending_confirmation';
  const canCancel = order.can_cancel;

  useEffect(() => {
    if (showCancelModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCancelModal]);

  const handleCancel = () => {
    onCancel(cancelReason);
    setShowCancelModal(false);
    setCancelReason('');
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3">
        {canConfirm && (
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <span className="animate-spin">⏳</span>
            ) : (
              <>
                <span>✅</span>
                <span>Подтвердить заказ</span>
              </>
            )}
          </button>
        )}

        {canCancel && (
          <button
            onClick={() => setShowCancelModal(true)}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2"
          >
            <span>❌</span>
            <span>Отменить заказ</span>
          </button>
        )}
      </div>

      {/* Статус, когда действия недоступны */}
      {!canConfirm && !canCancel && (
        <div className="bg-gray-100 dark:bg-gray-700 rounded-xl p-4 text-center">
          <p className="text-gray-600 dark:text-gray-300">
            {order.status === 'completed' && '✅ Заказ завершён'}
            {order.status === 'cancelled' && '❌ Заказ отменён'}
            {order.status === 'refunded' && (order.order_type === 'booking_no_prepayment' ? '❌ Заказ отменён' : '💰 Средства возвращены')}
            {order.status === 'confirmed' && '✅ Заказ подтверждён'}
            {order.status === 'in_progress' && '🔧 Заказ в работе'}
            {order.status === 'ready' && '📦 Заказ готов'}
          </p>
        </div>
      )}

      {/* Модальное окно отмены */}
      {showCancelModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">
              ❌ Отмена заказа
            </h3>

            {order.payment_status?.has_payment && ['succeeded', 'processing'].includes(order.payment_status?.status) && order.order_type !== 'booking_no_prepayment' && (
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  ⚠️ Средства будут автоматически возвращены покупателю
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Причина отмены (необязательно)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Например: по просьбе покупателя..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                rows={3}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                💡 Ваш комментарий увидит покупатель в уведомлении
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-semibold py-3 px-6 rounded-xl transition-colors duration-200"
              >
                Назад
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-3 px-6 rounded-xl transition-colors duration-200"
              >
                {isLoading ? 'Отмена...' : 'Подтвердить отмену'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
